import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDownLeft, ArrowUpRight, Loader2, Mail, MessageSquare, Plus, Reply } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Panel } from "@/components/si/Bits";
import { MessageDialog } from "@/components/si/MessageDialog";
import { api, errMsg } from "@/lib/api";
import { SI } from "@/lib/sourcing";
import { MSG_EVENT, emitMessages, threadStatus } from "@/lib/msgTemplates";

const VIA = { google_voice_manual: "via Google Voice", manual: "logged by hand", synced: "synced", app: "from the app" };
const sel = "h-8 rounded-md border border-[#27272A] bg-[#121215] px-2 text-xs text-zinc-200";

const ReplyForm = ({ c, pid, last, onDone }) => {
  const [v, setV] = useState({ channel: last?.channel || "email", body: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!v.body.trim()) return toast.error("Paste or type their reply first.");
    setBusy(true);
    try {
      await api.post(`${SI}/candidates/${c.id}/messages`, { direction: "inbound", channel: v.channel, from: last?.to || "", body: v.body.trim(), project_id: pid, sent_via: "manual", in_reply_to: last?.id });
      toast.success("Reply logged"); emitMessages(c.id); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 space-y-2 rounded-xl border border-[#27272A] p-2" data-testid="si-hist-reply-form">
      <select value={v.channel} onChange={(e) => setV({ ...v, channel: e.target.value })} className={sel} data-testid="si-hist-reply-channel"><option value="email">Email reply</option><option value="sms">Text reply</option></select>
      <Textarea rows={3} value={v.body} onChange={(e) => setV({ ...v, body: e.target.value })} placeholder={`What did ${c.full_name?.split(" ")[0] || "they"} say?`} className="text-xs" data-testid="si-hist-reply-body" />
      <div className="flex gap-2"><Button size="sm" onClick={save} disabled={busy} className="h-8 bg-[#D4AF37] text-black hover:bg-[#F3C649]" data-testid="si-hist-reply-save">{busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Log reply</Button>
        <Button size="sm" variant="ghost" onClick={onDone} className="h-8 text-zinc-400" data-testid="si-hist-reply-cancel">Cancel</Button></div>
    </div>
  );
};

const Item = ({ m, onReply }) => {
  const inb = m.direction === "inbound";
  const Ch = m.channel === "sms" ? MessageSquare : Mail;
  return (
    <div className={`rounded-xl border p-2.5 text-xs ${inb ? "mr-6 border-emerald-500/25 bg-emerald-500/5" : "ml-6 border-[#27272A] bg-[#121215]"}`} data-testid={`si-hist-item-${m.id}`}>
      <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-zinc-500">
        {inb ? <ArrowDownLeft className="h-3 w-3 text-emerald-300" /> : <ArrowUpRight className="h-3 w-3 text-[#D4AF37]" />}<Ch className="h-3 w-3" />
        <span className="text-zinc-300">{inb ? `Reply${m.from ? ` from ${m.from}` : ""}` : `${m.author_name ? `${m.author_name} → ` : "To "}${m.to || ""}`}</span>
        <span>· {new Date(m.created_at).toLocaleString()}</span>{VIA[m.sent_via] && <span>· {VIA[m.sent_via]}</span>}
        {["bounced", "failed"].includes(m.status) && <span className="text-red-300">· {m.status}</span>}
        {!inb && <button onClick={() => onReply(m)} className="ml-auto inline-flex items-center gap-1 text-[#c79bff] hover:underline" data-testid={`si-hist-followup-${m.id}`}><Reply className="h-3 w-3" />Follow up</button>}
      </div>
      {m.subject && <p className="mt-1 font-medium text-zinc-100">{m.subject}</p>}
      <p className="mt-1 whitespace-pre-wrap text-zinc-300">{m.body}</p>
    </div>
  );
};

// §71 Every email/text sent to the candidate plus their replies, newest first, with an automatic status tag.
export const MessageHistory = ({ c, pid, readOnly }) => {
  const [items, setItems] = useState(null);
  const [missing, setMissing] = useState(false);
  const [logging, setLogging] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = () => api.get(`${SI}/candidates/${c.id}/messages`).then((r) => setItems(r.data?.items || [])).catch((e) => { setMissing(e.response?.status === 404); setItems([]); });
  useEffect(() => { load(); const f = (e) => e.detail === c.id && load(); window.addEventListener(MSG_EVENT, f); return () => window.removeEventListener(MSG_EVENT, f); }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const list = [...(items || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const st = threadStatus(list);
  const lastOut = list.find((m) => m.direction !== "inbound");
  const tag = st && <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${st.cls}`} data-testid="si-hist-status">{st.label}</span>;
  return (
    <Panel title={`Messages${list.length ? ` · ${list.length}` : ""}`} testid="si-msg-history" right={tag}>
      {items === null ? <Loader2 className="h-4 w-4 animate-spin text-[#D4AF37]" /> : missing ? <p className="text-xs text-zinc-500" data-testid="si-hist-missing">Message history isn't live on the backend yet (spec §71).</p>
        : !list.length ? <p className="text-xs text-zinc-500" data-testid="si-hist-empty">No messages yet. Tap an email or phone chip above to reach out.</p>
          : <div className="max-h-96 space-y-2 overflow-y-auto pr-1">{list.map((m) => <Item key={m.id} m={m} onReply={(x) => setMsg({ channel: x.channel, to: x.to, subject: x.subject ? (/^re:/i.test(x.subject) ? x.subject : `Re: ${x.subject}`) : "" })} />)}</div>}
      {!readOnly && !missing && list.length > 0 && (logging ? <ReplyForm c={c} pid={pid} last={lastOut} onDone={() => setLogging(false)} />
        : <Button size="sm" variant="outline" onClick={() => setLogging(true)} className="mt-2 h-8 border-[#27272A] text-zinc-200" data-testid="si-hist-log-reply"><Plus className="h-3.5 w-3.5" />Log a reply</Button>)}
      <MessageDialog c={c} pid={pid} msg={msg} onClose={() => setMsg(null)} />
    </Panel>
  );
};
